/**
 * AURA Button Group Control
 * Renders a row of mutually exclusive option buttons (e.g. text alignment).
 */

export interface ButtonGroupOption {
  value: string;
  label: string;
  icon?: string;
  ariaLabel?: string;
}

export interface ButtonGroupOptions {
  id: string;
  label: string;
  options: ButtonGroupOption[];
  value?: string;
  onChange?: (value: string) => void;
}

const BUTTON_GROUP_STYLES = `
  .aura-btn-group {
    display: flex;
    flex-direction: column;
    gap: var(--aura-spacing-sm);
    padding: var(--aura-spacing-sm) 0;
    margin-top: var(--aura-spacing-md);
  }

  .aura-btn-group__label {
    font-size: var(--aura-font-size-md);
    color: var(--aura-text);
    font-weight: 500;
  }

  .aura-btn-group__options {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: var(--aura-spacing-sm);
  }

  .aura-btn-group__btn {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 6px;
    padding: var(--aura-spacing-md) var(--aura-spacing-xs);
    min-height: 90px;
    border-radius: var(--aura-radius-lg);
    background: var(--aura-bg-secondary);
    color: var(--aura-text-muted);
    font-size: var(--aura-font-size-sm);
    border: 2px solid transparent;
    cursor: pointer;
    transition: all var(--aura-transition-fast);
    text-align: center;
  }

  .aura-btn-group__btn:hover {
    background: var(--aura-bg-hover);
    border-color: var(--aura-border);
  }

  .aura-btn-group__btn:focus-visible {
    outline: 2px solid var(--aura-border-focus);
    outline-offset: 2px;
  }

  .aura-btn-group__btn[aria-pressed="true"] {
    background: var(--aura-primary-light);
    color: var(--aura-primary);
    border-color: var(--aura-primary);
  }

  .aura-btn-group__btn[aria-pressed="true"]:hover {
    background: var(--aura-primary-light);
    border-color: var(--aura-primary-hover);
  }

  .aura-btn-group__icon {
    width: 28px;
    height: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .aura-btn-group__icon svg {
    width: 28px;
    height: 28px;
  }

  .aura-btn-group__btn[aria-pressed="true"] .aura-btn-group__icon {
    color: var(--aura-primary);
  }

  .aura-btn-group__btn span:last-child {
    font-weight: 500;
    line-height: 1.2;
  }

  .aura-btn-group__btn[aria-pressed="true"] span:last-child {
    font-weight: 600;
  }
`;

export function createButtonGroup(
  options: ButtonGroupOptions
): { element: HTMLElement; styles: string; setValue: (v: string) => void } {
  const container = document.createElement('div');
  container.className = 'aura-btn-group';

  const label = document.createElement('span');
  label.className = 'aura-btn-group__label';
  label.id = `${options.id}-label`;
  label.textContent = options.label;

  const group = document.createElement('div');
  group.className = 'aura-btn-group__options';
  group.setAttribute('role', 'group');
  group.setAttribute('aria-labelledby', label.id);

  const buttons: HTMLButtonElement[] = [];

  for (const opt of options.options) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'aura-btn-group__btn';
    btn.dataset.value = opt.value;
    btn.setAttribute('aria-pressed', String(opt.value === (options.value ?? '')));
    btn.setAttribute('aria-label', opt.ariaLabel ?? opt.label);

    if (opt.icon) {
      const icon = document.createElement('span');
      icon.className = 'aura-btn-group__icon';
      icon.setAttribute('aria-hidden', 'true');
      icon.innerHTML = opt.icon;
      btn.appendChild(icon);
    }

    const text = document.createElement('span');
    text.textContent = opt.label;
    btn.appendChild(text);

    btn.addEventListener('click', () => {
      setActiveValue(opt.value);
      options.onChange?.(opt.value);
    });

    buttons.push(btn);
    group.appendChild(btn);
  }

  container.appendChild(label);
  container.appendChild(group);

  function setActiveValue(value: string): void {
    for (const btn of buttons) {
      btn.setAttribute('aria-pressed', String(btn.dataset.value === value));
    }
  }

  return {
    element: container,
    styles: BUTTON_GROUP_STYLES,
    setValue: setActiveValue,
  };
}

export { BUTTON_GROUP_STYLES };
